import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import Fade from '@mui/material/Fade';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import BiotechIcon from '@mui/icons-material/Biotech';
import SecurityIcon from '@mui/icons-material/Security';
import LanguageIcon from '@mui/icons-material/Language';

// Animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const ContactContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  animation: `${fadeIn} 0.5s ease-out`,
}));

const ContactInfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
}));

// FAQ data
const faqData = [
  {
    question: 'How do I schedule an appointment?',
    answer: 'You can schedule an appointment through your dashboard by clicking on the "Schedule Appointment" button. You can choose your preferred doctor, date, and time based on availability.'
  },
  {
    question: 'How do video consultations work?',
    answer: 'Video consultations are conducted through our secure platform. At the scheduled time, log in to your account, go to your dashboard, and click on the "Start Video Call" button. Make sure your camera and microphone are working properly before the call.'
  },
  {
    question: 'What should I do if I need to cancel or reschedule an appointment?',
    answer: 'You can cancel or reschedule an appointment through your dashboard. Go to "My Appointments," find the appointment you want to change, and click on the "Cancel" or "Reschedule" button. Please note that cancellations should be made at least 24 hours in advance to avoid any fees.'
  },
  {
    question: 'How can I access my medical records?',
    answer: 'Your medical records are available in your dashboard under "Medical Records." You can view and download your records at any time. If you need specific records that are not available, please contact our support team.'
  },
  {
    question: 'Is my personal and medical information secure?',
    answer: 'Yes, we take data security very seriously. All your personal and medical information is encrypted and stored securely. We comply with all relevant healthcare privacy regulations, including HIPAA. You can review our privacy policy for more details.'
  }
];

const ExternalContact = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal',
    attachment: null,
  });
  
  // Form submission state
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle file attachment
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size exceeds 5MB limit');
        return;
      }
      setFormData(prev => ({ ...prev, attachment: file }));
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (import.meta.env.DEV) console.log('Form submitted:', formData);
    // Here you would typically make an API call to send the message
    
    // Simulate successful submission
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
      priority: 'normal',
      attachment: null,
    });
    
    // Reset file input
    const fileInput = document.getElementById('attachment-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSubmitted(false);
    setError(null);
  };
  
  return (
    <ContactContainer>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: '#0A4A6E', 
          color: 'white',
          pt: { xs: 10, md: 12 },
          pb: { xs: 10, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'linear-gradient(to right, rgba(10, 74, 110, 0.9), rgba(10, 74, 110, 0.7))',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(10, 74, 110, 0.85), rgba(10, 74, 110, 0.65))', // Gradient overlay
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={900}>
                <Box sx={{ position: 'relative', zIndex: 1 }} role="banner" aria-label="Contact Us Hero Section">
                  <Box
                    component="img"
                    src="/images/logo.svg"
                    alt="Alpha01 Medical Consultants Logo - Telemedicine Platform"
                    aria-label="Alpha01 Medical Consultants Logo"
                    sx={{
                      width: { xs: 200, md: 240 },
                      height: 'auto',
                      mb: { xs: 3, md: 4 },
                      mx: 'auto',
                      display: 'block',
                      borderRadius: 2,
                      padding: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }}
                  />
                  <Typography 
                    variant="h2" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 800,
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      textAlign: { xs: 'center', md: 'left' }
                    }}
                  >
                    Contact Us
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                      textAlign: { xs: 'center', md: 'left' }
                    }}
                  >
                    We're here to help with any questions about our telemedicine services. Reach out to our team for support.
                  </Typography>
                  {/* Navigation Links */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 4, md: 4 }, 
                    flexWrap: 'wrap', 
                    mt: 2,
                    justifyContent: { xs: 'center', md: 'flex-start' }
                  }}>
                    <Link to="/" style={{ textDecoration: 'none' }}>
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 500, px: 2, py: 1, borderRadius: 2, minWidth: 56, minHeight: 48, '&:hover': { color: '#1DB4A3', transform: 'scale(1.08)', transition: 'all 0.2s cubic-bezier(.4,2,.6,1)' }, '&:focus': { outline: '3px solid #1DB4A3', outlineOffset: '2px', backgroundColor: 'rgba(29,180,163,0.08)' } }}>
                        Home
                      </Typography>
                    </Link>
                    <Link to="/features" style={{ textDecoration: 'none' }}>
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 500, px: 2, py: 1, borderRadius: 2, minWidth: 56, minHeight: 48, '&:hover': { color: '#1DB4A3', transform: 'scale(1.08)', transition: 'all 0.2s cubic-bezier(.4,2,.6,1)' }, '&:focus': { outline: '3px solid #1DB4A3', outlineOffset: '2px', backgroundColor: 'rgba(29,180,163,0.08)' } }}>
                        Features
                      </Typography>
                    </Link>
                    <Link to="/about" style={{ textDecoration: 'none' }}>
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 500, px: 2, py: 1, borderRadius: 2, minWidth: 56, minHeight: 48, '&:hover': { color: '#1DB4A3', transform: 'scale(1.08)', transition: 'all 0.2s cubic-bezier(.4,2,.6,1)' }, '&:focus': { outline: '3px solid #1DB4A3', outlineOffset: '2px', backgroundColor: 'rgba(29,180,163,0.08)' } }}>
                        About
                      </Typography>
                    </Link>
                    <Link to="/external-contact" style={{ textDecoration: 'none' }}>
                      <Typography variant="body1" sx={{ color: '#1DB4A3', fontWeight: 700, px: 2, py: 1, borderRadius: 2, minWidth: 56, minHeight: 48, '&:hover': { color: '#1DB4A3', transform: 'scale(1.08)', transition: 'all 0.2s cubic-bezier(.4,2,.6,1)' }, '&:focus': { outline: '3px solid #E53935', outlineOffset: '2px', backgroundColor: 'rgba(229,57,53,0.08)' } }}>
                        Contact
                      </Typography>
                    </Link>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                alignItems: 'center', 
                gap: 3, 
                width: '100%' 
              }}>
                <Box 
                  component="img"
                  src="/images/contactus.png"
                  alt="Contact Us"
                  sx={{ 
                    width: { xs: '100%', md: '45%' },
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                    mb: { xs: 3, md: 0 }
                  }}
                />
                <Box 
                  sx={{ 
                    width: { xs: '100%', md: '55%' },
                    p: { xs: 3, md: 4 },
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                      Get in Touch
                    </Typography>
                    <ContactInfoItem>
                      <ContactIcon sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
                        <EmailIcon />
                      </ContactIcon>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'white' }}>Email</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          support@alpha01med.com
                        </Typography>
                      </Box>
                    </ContactInfoItem>
                    <ContactInfoItem>
                      <ContactIcon sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
                        <PhoneIcon />
                      </ContactIcon>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'white' }}>Phone</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          (555) 123-4567
                        </Typography>
                      </Box>
                    </ContactInfoItem>
                    <ContactInfoItem>
                      <ContactIcon sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
                        <LocationOnIcon />
                      </ContactIcon>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'white' }}>Address</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          123 Medical Center Drive<br />
                          San Francisco, CA 94143
                        </Typography>
                      </Box>
                    </ContactInfoItem>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
        
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            bgcolor: '#1DB4A3', // Logo teal
            opacity: 0.2,
            zIndex: 0
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: '#E53935', // Logo red
            opacity: 0.2,
            zIndex: 0
          }} 
        />
      </Box>
      
      {/* Contact Form Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: { xs: 8, md: 12 }, width: '100vw' }}>
        <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Grid container spacing={6} sx={{ maxWidth: '100%', mx: 'auto' }}>
            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <Paper 
                elevation={1} 
                sx={{ 
                  borderRadius: 4,
                  p: 4,
                  mb: { xs: 4, md: 0 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, color: '#0A4A6E' }}>
                  Send Us a Message
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        helperText="Please select the subject of your message"
                      >
                        <MenuItem value="general">General Inquiry</MenuItem>
                        <MenuItem value="technical">Technical Support</MenuItem>
                        <MenuItem value="billing">Billing Inquiry</MenuItem>
                        <MenuItem value="medical">Medical Question</MenuItem>
                        <MenuItem value="appointment">Appointment Issue</MenuItem>
                        <MenuItem value="feedback">Feedback</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        multiline
                        rows={6}
                        variant="outlined"
                        helperText="Please provide as much detail as possible"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        variant="outlined"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </TextField>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        sx={{ height: '56px', width: '100%' }}
                      >
                        {formData.attachment ? formData.attachment.name : 'Attach File'}
                        <input
                          id="attachment-input"
                          type="file"
                          hidden
                          onChange={handleFileChange}
                        />
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max file size: 5MB
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          startIcon={<SendIcon />}
                          size="large"
                          sx={{ 
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            bgcolor: '#0A4A6E',
                            '&:hover': {
                              bgcolor: '#083a58',
                            },
                          }}
                        >
                          Send Message
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
            
            {/* Hours and Response Time */}
            <Grid item xs={12} md={4} sx={{ width: '100%' }}>
              <Paper 
                elevation={1} 
                sx={{ 
                  borderRadius: 4,
                  p: 4,
                  mb: 4,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#0A4A6E' }}>
                  Hours of Operation
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Monday - Friday
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight={600}>
                      8:00 AM - 8:00 PM
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Saturday
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight={600}>
                      9:00 AM - 5:00 PM
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Sunday
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight={600}>
                      Closed
                    </Typography>
                  </Grid>
                </Grid>
                
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<ChatIcon />}
                  sx={{ mt: 4 }}
                >
                  Start Live Chat
                </Button>
                <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                  Available during business hours
                </Typography>
              </Paper>
              
              <Paper 
                elevation={1} 
                sx={{ 
                  borderRadius: 4,
                  p: 4,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#0A4A6E' }}>
                  Response Time
                </Typography>
                <Typography variant="body1" paragraph>
                  We strive to respond to all inquiries within 24 hours during business days.
                </Typography>
                <Typography variant="body1">
                  For urgent matters, please call our support line or select "Urgent" priority when submitting your message.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* FAQ Section */}
      <Box sx={{ bgcolor: '#f7fafc', py: { xs: 8, md: 12 }, width: '100vw', mt: 6 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 2,
              color: '#0A4A6E',
              fontWeight: 700
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: 8,
              color: '#666',
              maxWidth: 800,
              mx: 'auto',
              fontWeight: 400
            }}
          >
            Find quick answers to common questions about our telemedicine services.
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={10} lg={8}>
              <Paper 
                elevation={1} 
                sx={{ 
                  borderRadius: 4,
                  p: 4,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: '#fff',
                }}
              >
              {faqData.map((faq, index) => (
                <Accordion 
                  key={index} 
                  sx={{ 
                    mb: 2, 
                    boxShadow: 'none', 
                    '&:before': { display: 'none' },
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`faq-content-${index}`}
                    id={`faq-header-${index}`}
                    sx={{ 
                      bgcolor: '#f9f9f9',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      p: { xs: 2, md: 3 }
                    }}
                  >
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: '#0A4A6E',
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Can't find what you're looking for?
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    bgcolor: '#0A4A6E',
                    '&:hover': {
                      bgcolor: '#083a58',
                    },
                  }}
                >
                  Contact Our Support Team
                </Button>
              </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Map Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: { xs: 8, md: 12 }, width: '100vw' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 2,
              color: '#0A4A6E',
              fontWeight: 700
            }}
          >
            Visit Our Office
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: 8,
              color: '#666',
              maxWidth: 800,
              mx: 'auto',
              fontWeight: 400
            }}
          >
            While we primarily provide telemedicine services, you can also visit our physical location.
          </Typography>
          
          <Paper 
            elevation={1} 
            sx={{ 
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              height: 450,
              width: '100%',
              bgcolor: '#e0e0e0', // Placeholder color for map
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5" color="text.secondary">
              Map Placeholder
            </Typography>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer */}
      <Box sx={{ bgcolor: '#0A4A6E', color: 'white', py: 6, borderTop: '1px solid rgba(255,255,255,0.1)', width: '100vw' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center" alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Alpha01 Telemedicine
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, maxWidth: 250 }}>
                Providing intelligent healthcare delivery through technology, connecting patients with providers seamlessly.
              </Typography>
              <Box 
                component="img"
                src="/images/logo.png"
                alt="Alpha01 Medical Consultants"
                sx={{ 
                  maxWidth: 120,
                  height: 'auto',
                  mt: 2,
                  opacity: 0.9,
                  borderRadius: 2, // Rounded edges for the logo
                  padding: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Platform
              </Typography>
              <Typography variant="body2" component={Link} to="/features" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Features
              </Typography>
              <Typography variant="body2" component={Link} to="/how-it-works" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                How It Works
              </Typography>
              <Typography variant="body2" component={Link} to="/security" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Security
              </Typography>
              <Typography variant="body2" component={Link} to="/integrations" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Integrations
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Company
              </Typography>
              <Typography variant="body2" component={Link} to="/about" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                About Us
              </Typography>
              <Typography variant="body2" component={Link} to="/contact" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Contact
              </Typography>
              <Typography variant="body2" component={Link} to="/careers" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Careers
              </Typography>
              <Typography variant="body2" component={Link} to="/blog" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Blog
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Resources
              </Typography>
              <Typography variant="body2" component={Link} to="/help" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Help Center
              </Typography>
              <Typography variant="body2" component={Link} to="/providers" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                For Providers
              </Typography>
              <Typography variant="body2" component={Link} to="/patients" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                For Patients
              </Typography>
              <Typography variant="body2" component={Link} to="/partners" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Partners
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Legal
              </Typography>
              <Typography variant="body2" component={Link} to="/terms" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Terms of Service
              </Typography>
              <Typography variant="body2" component={Link} to="/privacy" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" component={Link} to="/hipaa" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                HIPAA Compliance
              </Typography>
              <Typography variant="body2" component={Link} to="/accessibility" sx={{ display: 'block', mb: 1, color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Accessibility
              </Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          
          <Typography variant="body2" align="center" sx={{ opacity: 0.8 }}>
            © {new Date().getFullYear()} Alpha01 Medical Consultants. All rights reserved.
          </Typography>
        </Container>
      </Box>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={submitted || !!error}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={error ? 'error' : 'success'} 
          sx={{ width: '100%' }}
        >
          {error ? error : 'Your message has been sent successfully. We will get back to you soon.'}
        </Alert>
      </Snackbar>
    </ContactContainer>
  );
};

export default ExternalContact;
